import type {
  GuidedModeDefinition,
  GuidedModeQuestionDefinition,
  GuidedModeStep,
} from '../types/guided-mode.types'

export const DEMOGRAPHIC_CONCEPT_IDS: ReadonlySet<string> = new Set([
  'concept.patient_id',
  'concept.age',
  'concept.gender',
  'concept.maternal_status',
])

export interface GuidedModeQuestionEntry {
  step: GuidedModeStep
  question: GuidedModeQuestionDefinition
}

export function questionForStep(
  definition: GuidedModeDefinition | null | undefined,
  step: GuidedModeStep,
): GuidedModeQuestionDefinition | undefined {
  return step.questionId ? definition?.questions[step.questionId] : undefined
}

export function findDemographicQuestion(
  definition: GuidedModeDefinition | null | undefined,
  conceptId: string,
): GuidedModeQuestionEntry | null {
  const group = definition?.assessment?.steps.find(step => (
    step.kind === 'group' && step.id === 'group.demographics'
  ))
  for (const step of group?.steps || []) {
    const question = questionForStep(definition, step)
    if (question?.conceptId === conceptId) return { step, question }
  }
  return null
}

export function assessmentQuestionSteps(
  definition: GuidedModeDefinition | null | undefined,
  excludedDataPointCodes: readonly string[] = [],
  focusedPanelNames?: readonly string[],
): GuidedModeStep[] {
  if (!definition) return []
  const excludedCodes = new Set(excludedDataPointCodes)
  const focusedPanels = focusedPanelNames ? new Set(focusedPanelNames) : null
  const matchedPanels = new Set<string>()

  const belongsToFocusedPanel = (step: GuidedModeStep, parentGroupId?: string): boolean => {
    if (!focusedPanels) return true
    const originalGroupId = step.sourceGroupId || parentGroupId
    for (const panel of focusedPanels) {
      if (
        originalGroupId === `group.${panel}`
        || step.id === `group.${panel}`
        || step.id === `step.${panel}`
      ) {
        matchedPanels.add(panel)
        return true
      }
    }
    return false
  }

  // Comprehensive mode uses the full questionnaire. Focused mode keeps only the
  // panels returned by MRX, without the legacy mode-selector concepts.
  const eligibleSteps = (steps: GuidedModeStep[], parentGroupId?: string): GuidedModeStep[] => steps.flatMap(step => {
    if (step.kind === 'group') belongsToFocusedPanel(step, parentGroupId)
    const focusedPanelMatches = step.kind === 'group'
      ? true
      : belongsToFocusedPanel(step, parentGroupId)
    const question = questionForStep(definition, step)
    if (question && ['concept.streamlined_choice', 'concept.streamlined_options'].includes(question.conceptId)) {
      return []
    }
    const dataPointCode = question && definition?.concepts[question.conceptId]?.code?.code
    if (dataPointCode && excludedCodes.has(dataPointCode)) return []
    if (step.kind === 'group') {
      const children = eligibleSteps(step.steps || [], step.id)
      return children.length ? [{ ...step, steps: children }] : []
    }
    if (!focusedPanelMatches) return []
    return [step]
  })

  const result = eligibleSteps(definition?.assessment?.steps || []).flatMap(step => {
    if (step.id === 'group.demographics') {
      return (step.steps || []).filter(nestedStep => {
        const question = questionForStep(definition, nestedStep)
        return question && !DEMOGRAPHIC_CONCEPT_IDS.has(question.conceptId)
      })
    }
    if (step.kind === 'group') {
      const containsAssessmentQuestion = (step.steps || []).some(nestedStep => {
        const question = questionForStep(definition, nestedStep)
        return question && !DEMOGRAPHIC_CONCEPT_IDS.has(question.conceptId)
      })
      return containsAssessmentQuestion ? [step] : []
    }
    const question = questionForStep(definition, step)
    return !question || !DEMOGRAPHIC_CONCEPT_IDS.has(question.conceptId) ? [step] : []
  })

  const ignoredLegacyPanels = new Set(['rdt_choice', 'rdt_status', 'rdt_bioline_capture_practitioner'])
  for (const observation of definition.fixedObservations || []) ignoredLegacyPanels.add(observation.name)
  const unmappedPanel = focusedPanelNames?.find(panel => (
    !matchedPanels.has(panel) && !ignoredLegacyPanels.has(panel)
  ))
  if (unmappedPanel) throw new Error(`Unmapped focused identifier ${unmappedPanel}`)

  return result
}
